/**
 * Composable for converting one type to another type. You can add customized rule here as well
 */
import moment from 'moment';
import { domainStore } from '@/store/domain-store';
export default function () {
  /**
   * Transform an object of type any into a boolean true/false. Evaluations include data such as 1, "true" being returned as true, etc.
   * Passing a boolean parameter will just return itself
   *
   * @param otherType object of any type
   * @param defaultVal if the type is not supported, this value will be returned instead
   * @returns true|false
   */
  function toBool(otherType: any, defaultVal = true) {
    if (typeof otherType === 'string') {
      return otherType == null ||
        otherType == undefined ||
        otherType == '' ||
        otherType == '0' ||
        otherType.toLowerCase() == 'false'
        ? false
        : true;
    } else if (typeof otherType === 'number') {
      return otherType > 0;
    } else if (typeof otherType === 'boolean') {
      return otherType;
    } else if (otherType == null) {
      return false;
    }
    return defaultVal;
  }

  function toArray(otherType: any) {
    if (otherType == undefined || otherType == null) return [];
    if (Array.isArray(otherType)) return otherType;
    return Array.of(otherType);
  }

  function bytesToHumanReadable(bytes: number, decimals = 2) {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
  }

  // convert UTC to local time (DB saves UTC dates)
  function UTCtoLocalTime(utcTime: any) {
    return moment.utc(utcTime).local().format();
  }
  function formattedDate(value: any, dateOnly = false, monthOnly = false) {
    moment.locale('de');
    if (domainStore.getState().language && domainStore.getState().language != 'de') {
      moment.locale(domainStore.getState().language);
    }
    // change to L, and use right country-locale to auto-format correct dates
    if (value) {
      if (dateOnly) {
        return moment(String(value)).format('L');
        // previously: return moment(String(value)).format('DD.MM.YYYY');
      } else if (monthOnly) {
        // keep custom formatting for month/year
        return moment(String(value)).format('MMM YYYY');
      } else {
        return moment(String(value)).format('LLLL');
        // previously:
        // const dateString = moment(String(value)).format('DD.MM.YYYY');
        // const timeString = moment(String(value)).format('HH:mm');
        // return dateString + ' um ' + timeString + ' Uhr';
      }
    }
  }
  function roundDecimals(value: number, decimals = 2) {
    if (!value) {
      return 0;
    }
    return value.toFixed(decimals).replace(/[.,]0+$/, '');
  }

  function localeDateStringLong(date: string) {
    const localeDate = UTCtoLocalTime(date);
    return new Date(localeDate)
      .toLocaleDateString('de-DE', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      })
      .replace(/,/g, ' um')
      .concat(' Uhr');
  }
  function localeDateStringShort(date: string) {
    const localeDate = UTCtoLocalTime(date);
    return new Date(localeDate).toLocaleDateString('de-DE', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  }
  function convertArrayToApiParams(params?: Record<string, string | number | Array<string | number>>) {
    for (const property in params) {
      if (Array.isArray(params[property])) {
        params[property] = (params[property] as Array<string | number>).join(',');
      }
    }
    return params;
  }

  function timeDifference(date1: any) {
    moment.locale('de');
    const targetDate = moment(String(date1));
    const currentDay = moment();
    const diff = moment.duration(currentDay.diff(targetDate));
    if (diff.asYears() >= 1) {
      return `vor ${Math.floor(diff.asYears())} Jahr${diff.asYears() > 1 ? 'e' : ''}`;
    } else if (diff.asMonths() >= 1) {
      return `vor ${Math.floor(diff.asMonths())} Monat${diff.asMonths() > 1 ? 'e' : ''}`;
    } else if (diff.asDays() >= 1) {
      return `vor ${Math.floor(diff.asDays())} Tag${diff.asDays() > 1 ? 'e' : ''}`;
    } else if (diff.asHours() >= 1) {
      return `vor ${Math.floor(diff.asHours())} Stunde${diff.asHours() > 1 ? 'n' : ''}`;
    } else if (diff.asMinutes() >= 1) {
      return `vor ${Math.floor(diff.asMinutes())} Minute${diff.asMinutes() > 1 ? 'n' : ''}`;
    } else {
      return `vor ${Math.floor(diff.asSeconds())} Sekunde${diff.asSeconds() > 1 ? 'n' : ''}`;
    }
  }
  return {
    toBool,
    toArray,
    bytesToHumanReadable,
    formattedDate,
    UTCtoLocalTime,
    roundDecimals,
    localeDateStringLong,
    localeDateStringShort,
    convertArrayToApiParams,
    timeDifference,
  };
}
